import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';

import { DateHelper } from 'src/features/drilling-chart/shared/date-helper';
import { PadAttributeIcon } from 'src/features/pad-attribute-icon';
import { LoadingTitle } from 'src/shared/components/loading-title/loading-title';
import { useIsOutOfViewport } from 'src/shared/hooks/use-is-out-of-viewport';
import { hasValue } from 'src/shared/utils/common';
import { mergeRefs } from 'src/shared/utils/merge-refs';

import { PadCard } from '../../../../pad-card';
import type { PadRigOperation } from '../../../presets/drilling-rigs-chart/entities';
import type { RigsViewSettingsProvider } from '../../../presets/drilling-rigs-chart/rigs-view-settings-provider';
import { useMovableElement } from '../../../shared/use-movable-element';
import type { Viewport } from '../../../shared/viewport/viewport';
import { ChartTooltip } from '../../chart-tooltip/chart-tooltip';
import type { DndContextStore } from '../../editing/shared/entities/dnd-context.store';
import { useDraggable } from '../../editing/view/hooks/use-draggable';
import { PadTooltipInner } from '../../pad-rig-operation-wrapper';
import styles from '../../pad-rig-operation-wrapper/pad-rig-operation-wrapper.module.scss';
import { RSEReadinessIcons } from '../../rse-readiness-icons';
import { useDragging } from '../../sorting/hooks/use-dragging';

interface Props {
  item: PadRigOperation;
  horizontalViewport: Viewport;
  verticalViewport: Viewport;
  viewSettingsProvider: RigsViewSettingsProvider;
  onClick?: VoidFunction;
  className?: string;
  cardClassName?: string;
  headerClassName?: string;
  onRemoveRigOperationsList?: VoidFunction;
  isCompactView?: boolean;
}

const options: DndContextStore.DraggingOptions = {
  draggableClassName: styles.draggingItem,
};

export const DraggablePadRigOperationWrapper = observer(function DraggablePadRigOperationWrapper({
  item,
  horizontalViewport,
  verticalViewport,
  onClick,
  className,
  cardClassName,
  headerClassName,
  viewSettingsProvider,
  onRemoveRigOperationsList,
  isCompactView,
}: Props) {
  const { listeners } = useDraggable({
    id: item.getKey('draggable'),
    dataItem: item,
    onClick,
    options,
  });

  // To be dragged with sortable rig row.
  const { ref: draggingRef } = useDragging({
    id: item.getKey('dragging'),
    item,
  });

  const ref = useMovableElement({
    item,
    horizontalViewport,
    verticalViewport,
  });
  const isOutOfViewport = useIsOutOfViewport(item, horizontalViewport);

  const tooltipPlacement = isOutOfViewport ? 'rightTop' : 'top';

  const padAttributes = viewSettingsProvider.getPadAttributes(item);
  const rseReadinessVisibilitySettings = viewSettingsProvider.shownAttributesFields['padSettings'];

  const padIcons = (
    <div className={clsx(styles.iconsContainer, isCompactView && styles.iconsContainer__compact)}>
      <RSEReadinessIcons
        readinessData={item.rseReadiness}
        rseReadinessVisibilitySettings={rseReadinessVisibilitySettings}
      />
      {padAttributes?.map((attributeName) => (
        <PadAttributeIcon key={attributeName} iconName={attributeName} className={styles.icon} />
      ))}
    </div>
  );

  const wellRigOperationsStartDates = item.wellRigOperations.reduce((dates: number[], well) => {
    const date = viewSettingsProvider.getRigOperationStartUnix(well);

    if (hasValue(date)) {
      dates.push(date);
    }

    return dates;
  }, []);

  const firstWellStartDate = Math.min(...wellRigOperationsStartDates);

  const isRemovableRigOperation =
    firstWellStartDate !== Infinity && firstWellStartDate > DateHelper.dateToUnix(new Date());

  return (
    <ChartTooltip
      content={
        <PadTooltipInner
          title={<LoadingTitle field={viewSettingsProvider.getTitle(item)} />}
          subTitle={viewSettingsProvider.getPadTooltipDate(item)}
          attributes={viewSettingsProvider.getPadTooltipAttributes(item)}
        />
      }
      placement={tooltipPlacement}
    >
      <div key={item.id} ref={mergeRefs(ref, draggingRef)} className={clsx(className, styles.container)} {...listeners}>
        <PadCard
          title={<LoadingTitle field={viewSettingsProvider.getTitle(item)} />}
          extra={padIcons}
          attributes={viewSettingsProvider.getAttributes(item)}
          className={clsx(cardClassName, styles.card)}
          headerClassName={headerClassName}
          isCompactView={isCompactView}
          onDelete={isRemovableRigOperation ? onRemoveRigOperationsList : undefined}
        />
      </div>
    </ChartTooltip>
  );
});
